import React, { useState, useEffect } from 'react';

interface PresaleInfoProps {
  presaleInfo: {
    name: string;
    isActive: boolean;
    currentPhase: number;
    priceUsd: number;
    tokensAvailable: number;
    tokensSold: number;
    totalUsdRaised: number;
    timeRemaining: number;
    phaseEndTime: string;
  };
  onRefresh: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const PresaleInfo: React.FC<PresaleInfoProps> = ({ presaleInfo, onRefresh }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Update countdown timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      if (presaleInfo.timeRemaining > 0) {
        const totalSeconds = presaleInfo.timeRemaining;
        const days = Math.floor(totalSeconds / (24 * 3600));
        const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [presaleInfo.timeRemaining]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const progressPercentage = presaleInfo.tokensAvailable > 0 
    ? Math.min((presaleInfo.tokensSold / presaleInfo.tokensAvailable) * 100, 100)
    : 0;

  const formatNumber = (num: number, decimals: number = 2): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="presale-info">
      <div className="presale-header">
        <div className="title-section">
          <h2>{presaleInfo.name}</h2>
          <div className="status-badge">
            <span className={`status ${presaleInfo.isActive ? 'active' : 'inactive'}`}>
              {presaleInfo.isActive ? '🟢 Active' : '🔴 Inactive'}
            </span>
            <span className="phase-badge">Phase {presaleInfo.currentPhase}</span>
          </div>
        </div>
        
        <button 
          onClick={handleRefresh}
          className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
          disabled={isRefreshing}
        >
          {isRefreshing ? '⟳' : '🔄'} Refresh
        </button>
      </div>

      <div className="presale-grid">
        <div className="info-card price-card">
          <div className="card-header">
            <h3>Current Price</h3>
            <span className="card-icon">💰</span>
          </div>
          <div className="card-value">
            {formatCurrency(presaleInfo.priceUsd)}
            <span className="unit">per token</span>
          </div>
        </div>

        <div className="info-card progress-card">
          <div className="card-header">
            <h3>Tokens Sold</h3>
            <span className="card-icon">📊</span>
          </div>
          <div className="card-value">
            {formatNumber(presaleInfo.tokensSold, 0)}
            <span className="unit">/ {formatNumber(presaleInfo.tokensAvailable, 0)}</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="progress-text">
            {formatNumber(progressPercentage, 1)}% Complete
          </div>
        </div>

        <div className="info-card raised-card">
          <div className="card-header">
            <h3>Total Raised</h3>
            <span className="card-icon">💵</span>
          </div>
          <div className="card-value">
            {formatCurrency(presaleInfo.totalUsdRaised)}
          </div>
        </div>

        <div className="info-card countdown-card">
          <div className="card-header">
            <h3>Phase Ends In</h3>
            <span className="card-icon">⏰</span>
          </div>
          {presaleInfo.timeRemaining > 0 ? (
            <div className="countdown">
              <div className="time-segment">
                <span className="time-value">{timeLeft.days}</span>
                <span className="time-label">Days</span>
              </div>
              <div className="time-separator">:</div>
              <div className="time-segment">
                <span className="time-value">{String(timeLeft.hours).padStart(2, '0')}</span>
                <span className="time-label">Hours</span>
              </div>
              <div className="time-separator">:</div>
              <div className="time-segment">
                <span className="time-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                <span className="time-label">Min</span>
              </div>
              <div className="time-separator">:</div>
              <div className="time-segment">
                <span className="time-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
                <span className="time-label">Sec</span>
              </div>
            </div>
          ) : (
            <div className="countdown-ended">
              <span className="ended-text">Phase Ended</span>
            </div>
          )}
        </div>
      </div>

      <div className="presale-details">
        <div className="detail-item">
          <span className="detail-label">Phase End Time:</span>
          <span className="detail-value">
            {new Date(presaleInfo.phaseEndTime).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PresaleInfo;
